/**
 * @file Segment Hero
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Segment Hero component
 */
const vSegmentHero = () => {
	const segmentHero = document.querySelector( '.v-segment-hero' );

	if ( ! segmentHero ) {
		return;
	}

	gsap.registerPlugin( ScrollTrigger );
	const noGraphics = ! document.querySelector( '.v-segment-hero__animation-wrap ' );
	const smQuery    = window.matchMedia( '(max-width: 550px)' );

	gsap.to( '.v-segment-hero__content', {
		opacity:       1,
		y:             0,
		duration:      .6,
		ease:          'sine.out',
		scrollTrigger: {
			trigger: '.v-segment-hero__content-wrap',
			start:   '50% bottom',
		},
		onStart() {
			if ( noGraphics || graphicsEntrance || ! smQuery.matches ) {
				return;
			}

			enterGraphics();
		},
		onUpdate() {
			if ( noGraphics ) {
				return;
			}

			if ( this.progress() > .5 && ! graphicsEntrance ) {
				enterGraphics();
			}
		},
	} );

	// Bail out if no graphics
	if ( noGraphics ) {
		return;
	}

	const mainQuery = window.matchMedia( '(max-width: 1200px)' );
	const hasArrows = segmentHero.querySelector( '.v-segment-hero__animation' ).classList.contains( 'v-segment-hero__animation--with-arrows' );
	let graphicsEntrance;

	/**
		* Entrance for hero graphics
		*/
	const enterGraphics = () => {
		graphicsEntrance = gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: .9,
			},
			scrollTrigger: {
				trigger: '.v-segment-hero__animation-wrap',
				start:   '33.3333% bottom',
			},
			onComplete() {
				setTimeout( animateGraphics, 500 );
			},
		} )
			.to( '.v-segment-hero__animation', {
				duration: .6,
				y:        0,
			}, '>25%' )
			.to( '.v-segment-hero__animation', {
				opacity: 1,
			}, '>-50%' );
	};

	/**
		* Animation for chart
		*/
	const animateGraphics = () => {
		const columns      = segmentHero.querySelectorAll( '.v-segment-hero__animation-column' );
		let isAnimating    = false;
		let animationStart = 'bottom bottom';

		if ( mainQuery.matches ) {
			animationStart = smQuery.matches ? 'top bottom' : 'top+25% bottom';
		}

		const tl        = gsap.timeline( {
			defaults: {
				ease:     'sine.out',
				duration: hasArrows ? .6 : .9,
			},
			onStart: () => {
				isAnimating = true;
			},
			onComplete: () => {
				isAnimating = false;
			},
			scrollTrigger: {
				trigger: '.v-segment-hero__animation-wrap',
				start:   animationStart,
			},
		} );

		[ ...columns ].forEach( ( column, i ) => {
			const type = column.getAttribute( 'data-type' );

			tl.addLabel( `column-${ i }-start` );

			switch ( type ) {
				case 'graph':
					const graph       = column.querySelector( '.v-credit-graph' );
					const graphMin    = parseInt( graph.dataset.min );
					const graphMax    = parseInt( graph.dataset.max );
					const graphMask   = graph.querySelector( '.v-credit-graph__mask' );
					const graphNumber = graph.querySelector( '.v-credit-graph__number' );
					const change      = graph.querySelector( '.v-credit-graph__change' );

					const graphTargetNumber = parseInt( graphNumber.dataset.high );
					const graphAngle        = ( graphTargetNumber - graphMin ) / ( graphMax - graphMin ) * 360;

					tl
						.to( graphMask, {
							'--circle-progress': `${ graphAngle }deg`,
						}, i > 0 ? `column-0-start` : '' )
						.to( graphNumber, {
							textContent: graphTargetNumber,
							snap:        { textContent: 1 },
						}, '<' );

					if ( change ) {
						tl.to( change, {
							y:       0,
							opacity: 1,
						} );
					}

					tl.addLabel( `column-${ i }-end` );

					break;
				case 'number':
					const number       = column.querySelector( '.v-number-card' );
					const numberText   = number.querySelector( '.v-number-card__number' );
					const numberFormat = number.dataset.format;

					const numberTargetNum = numberFormat === 'percent' ? parseFloat( number.dataset.end ) : parseInt( number.dataset.end );
					const numberStartNum  = numberFormat === 'percent' ? parseFloat( number.dataset.start ) : parseInt( number.dataset.start );
					const numberObject = { value: numberStartNum };

					tl
						.to( numberObject, {
							value:    numberTargetNum,
							onUpdate: () => {
								numberText.textContent =
									numberFormat === 'percent'
										? numberObject.value.toFixed( 2 )
										: numberObject.value.toLocaleString( 'en-US', { maximumFractionDigits: 0 } );
							},
						}, i > 0 ? `column-0-start` : '' )
						.addLabel( `column-${ i }-end` );

					break;
				case 'house':
					const wrap          = column.querySelector( '.v-mortgage-graph' );
					const shouldAnimate = wrap.classList.contains( 'v-mortgage-graph--js' );

					if ( shouldAnimate ) {
						const graphic = column.querySelector( '.v-segment-hero__graphic--house-js' );
						const event   = new CustomEvent( 'vmortgageanimationplay' );
						tl.to( graphic, {
							opacity: 1,
							onStart: () => {
								document.dispatchEvent( event );
							},
						} );
					}

					tl.addLabel( `column-${ i }-end` );

					break;
				case 'arrow':
					const arrowStem = column.querySelectorAll( '.v-segment-hero__arrow-stem' );
					const arrowHead = column.querySelector( '.v-segment-hero__arrow-head' );
					const bracketQuery = window.matchMedia( '(min-width: 841px)' );
					const columnQuery  = window.matchMedia( '(max-width: 840px)' );

					[ ...arrowStem ].forEach( ( stem ) => {
						if ( ! mainQuery.matches ) {
							tl.to( stem, {
								scaleX: 1,
							}, arrowStem.length > 1 ? '>' : '>-=25%' );

							return;
						}

						if ( ( bracketQuery.matches && stem.classList.contains( 'v-segment-hero__arrow-stem--vertical' ) ) || columnQuery.matches ) {
							tl.to( stem, {
								scaleY: 1,
							}, arrowStem.length > 1 ? '>' : '>-=25%' );
						} else {
							tl.to( stem, {
								scaleX: 1,
							}, arrowStem.length > 1 ? '>' : '>-=25%' );
						}
					} );

					tl
						.to( arrowHead, {
							opacity:  1,
							duration: .15,
						} )
						.addLabel( `column-${ i }-end` );
					break;
				default:
					const housesGraphic = column.querySelector( '.v-segment-hero__graphic--houses-js' );
					const animateable   = housesGraphic.querySelectorAll( '.v-houses-graph__house--animate' );
					tl.to( housesGraphic, {
						opacity: 1,
					}, i > 0 ? `column-${ i - 1 }-end` : '' );

					if ( animateable.length ) {
						tl.to( animateable, {
							fill:    '#03d87c',
							stagger: .5,
						} );
					}

					tl.addLabel( `column-${ i }-end` );
					break;
			}
		} );

		mainQuery.addEventListener( 'change', () => {
			if ( isAnimating ) {
				tl.kill();
			}

			tl.restart();
		} );
	};
};

export default vSegmentHero;
